import {
    Button,
    Modal,
    Tooltip,
    Typography,
    Skeleton,
    theme,
    notification
} from "antd";
import {
    Clock,
    GitBranch,
    GitCommitHorizontal,
    GitPullRequest,
    GitMerge,
    Tag,
    Trash2,
    User,
    RefreshCw,
    Archive,
    Sparkles,
    Link2,
    ArrowRight,
    FileCode,
    ChevronDown,
    ChevronUp,
    Server
} from "lucide-react";
import React, { useState } from "react";
import { getEnvironmentService } from "../../../backend";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import type { PreviewWithGitMetadata } from "../../../types/preview-environments";
import { PREVIEW_STATUS_MAP, PLAYGROUND_HISTORY_LIMIT } from "../../../utils/preview-constants";
import { getPreviewDisplay } from "../../../utils/preview-utils";
import PreviewMetaItem from "../../../components/PreviewMetaItem";

dayjs.extend(relativeTime);

const { Text } = Typography;

const MONO_FONT = "'JetBrains Mono', 'Fira Code', 'SF Mono', Consolas, monospace";

interface Props {
    projectId: string;
    envId: string;
    previews: PreviewWithGitMetadata[];
    loading: boolean;
    onRefresh: () => void;
    clusters?: Array<{ id: string; name: string }>;
}

export default function PreviewPRList({ projectId, envId, previews, loading, onRefresh, clusters }: Props) {
    const { token } = theme.useToken();
    const [destroyingId, setDestroyingId] = useState<string | null>(null);
    const [historyExpanded, setHistoryExpanded] = useState(false);

    async function handleDestroy(preview: PreviewWithGitMetadata) {
        const d = getPreviewDisplay(preview);
        Modal.confirm({
            title: `Destroy preview for ${d.isPR ? `PR ${d.label}` : `tag ${d.label}`}?`,
            content: `This will permanently tear down the preview environment${d.title ? ` for "${d.title}"` : ""} and delete its namespace.`,
            okText: "Destroy",
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    setDestroyingId(preview.id);
                    await getEnvironmentService().deletePreview(envId, preview.id, projectId);
                    notification.success({ message: `Preview for ${d.isPR ? `PR ${d.label}` : `tag ${d.label}`} is being destroyed.` });
                    onRefresh();
                } catch (error: any) {
                    notification.error({
                        message: "Failed to destroy preview",
                        description: error?.response?.data?.message || error.message
                    });
                } finally {
                    setDestroyingId(null);
                }
            }
        });
    }

    // Sort active: failed first (needs attention), then by date
    const activePreviews = previews
        .filter(p => p.status !== "DESTROYED")
        .sort((a, b) => {
            if (a.status === "FAILED" && b.status !== "FAILED") return -1;
            if (a.status !== "FAILED" && b.status === "FAILED") return 1;
            return new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime();
        });
    const destroyedPreviews = previews.filter(p => p.status === "DESTROYED");

    if (loading) {
        return (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {[1, 2].map(i => (
                    <div key={i} style={{
                        background: token.colorBgBase,
                        borderRadius: 12,
                        padding: 20,
                        border: `1px solid ${token.colorBorderSecondary}`
                    }}>
                        <Skeleton active paragraph={{ rows: 2 }} />
                    </div>
                ))}
            </div>
        );
    }

    if (previews.length === 0) {
        return <EmptyState />;
    }

    const visibleHistory = historyExpanded
        ? destroyedPreviews
        : destroyedPreviews.slice(0, PLAYGROUND_HISTORY_LIMIT);

    return (
        <div>
            {/* Active Previews — Card grid */}
            <div style={{ marginBottom: 40 }}>
                <SectionHeader title="Active Previews" count={activePreviews.length} color="#059669" />
                {activePreviews.length > 0 ? (
                    <div style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(420px, 1fr))",
                        gap: 16
                    }}>
                        {activePreviews.map(preview => (
                            <ActivePreviewCard
                                key={preview.id}
                                preview={preview}
                                destroying={destroyingId === preview.id}
                                onDestroy={() => handleDestroy(preview)}
                                clusters={clusters}
                            />
                        ))}
                    </div>
                ) : (
                    <div style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "48px 24px",
                        borderRadius: 12,
                        border: `1px dashed ${token.colorBorderSecondary}`,
                        background: "#fafbfc"
                    }}>
                        <div style={{
                            width: 48,
                            height: 48,
                            borderRadius: 12,
                            background: "linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginBottom: 16
                        }}>
                            <GitPullRequest size={22} style={{ color: "#94a3b8" }} />
                        </div>
                        <Text strong style={{ fontSize: 15, color: "#374151", marginBottom: 4 }}>
                            No active previews
                        </Text>
                        <Text type="secondary" style={{ fontSize: 13 }}>
                            Open a pull request or push a matching tag to spin up a preview environment.
                        </Text>
                    </div>
                )}
            </div>

            {/* History — Compact table */}
            {destroyedPreviews.length > 0 && (
                <div>
                    <SectionHeader title="Recent History" count={destroyedPreviews.length} color="#9ca3af" />
                    <div style={{
                        border: `1px solid ${token.colorBorderSecondary}`,
                        borderRadius: 10,
                        overflow: "hidden",
                        background: token.colorBgBase
                    }}>
                        {visibleHistory.map((preview, i) => (
                            <HistoryRow
                                key={preview.id}
                                preview={preview}
                                isLast={i === visibleHistory.length - 1 && !(destroyedPreviews.length > PLAYGROUND_HISTORY_LIMIT && !historyExpanded)}
                            />
                        ))}
                        {destroyedPreviews.length > PLAYGROUND_HISTORY_LIMIT && (
                            <button
                                onClick={() => setHistoryExpanded(prev => !prev)}
                                style={{
                                    width: "100%",
                                    padding: "10px 16px",
                                    background: "#fafbfc",
                                    border: "none",
                                    borderTop: `1px solid ${token.colorBorderSecondary}`,
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 6,
                                    fontSize: 12,
                                    fontWeight: 500,
                                    color: "#6b7280",
                                    transition: "background 0.15s"
                                }}
                                onMouseEnter={e => { e.currentTarget.style.background = "#f3f4f6"; }}
                                onMouseLeave={e => { e.currentTarget.style.background = "#fafbfc"; }}
                            >
                                {historyExpanded ? (
                                    <>
                                        <ChevronUp size={14} />
                                        Show less
                                    </>
                                ) : (
                                    <>
                                        <ChevronDown size={14} />
                                        Show {destroyedPreviews.length - PLAYGROUND_HISTORY_LIMIT} more
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

/* ─── Section Header ──────────────────────────────────────────────────────── */

function SectionHeader({ title, count, color }: { title: string; count: number; color: string }) {
    return (
        <div style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 16
        }}>
            <Text style={{
                fontSize: 17,
                fontWeight: 600,
                color: "#111827",
                letterSpacing: "-0.01em"
            }}>
                {title}
            </Text>
            <span style={{
                fontSize: 13,
                fontWeight: 600,
                color: color,
                background: `${color}15`,
                borderRadius: 6,
                padding: "3px 10px",
                lineHeight: "20px",
                border: `1px solid ${color}30`
            }}>
                {count}
            </span>
        </div>
    );
}

/* ─── Empty State ─────────────────────────────────────────────────────────── */

function EmptyState() {
    return (
        <div style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "80px 32px",
            textAlign: "center",
            maxWidth: 520,
            margin: "0 auto"
        }}>
            <div style={{
                width: 80,
                height: 80,
                borderRadius: 20,
                background: "linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 24,
                position: "relative"
            }}>
                <GitPullRequest size={32} style={{ color: "#3b82f6" }} />
                <div style={{
                    position: "absolute",
                    bottom: -2,
                    right: -2,
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    background: "linear-gradient(135deg, #059669, #10b981)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 2px 8px rgba(5, 150, 105, 0.3)"
                }}>
                    <Sparkles size={14} style={{ color: "#fff" }} />
                </div>
            </div>

            <Text strong style={{ fontSize: 18, display: "block", marginBottom: 8, color: "#1f2937" }}>
                No preview environments yet
            </Text>
            <Text type="secondary" style={{ fontSize: 14, lineHeight: 1.6, marginBottom: 32 }}>
                Open a pull request to automatically create an isolated preview environment with its own URL.
            </Text>

            <div style={{
                display: "flex",
                gap: 16,
                width: "100%",
                justifyContent: "center",
                flexWrap: "wrap"
            }}>
                {[
                    { icon: <GitPullRequest size={16} />, label: "PR triggers deploy" },
                    { icon: <Link2 size={16} />, label: "Unique preview URL" },
                    { icon: <RefreshCw size={16} />, label: "Auto-cleanup on merge" },
                ].map((item) => (
                    <div key={item.label} style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "8px 14px",
                        borderRadius: 8,
                        background: "#f8fafc",
                        border: "1px solid #e2e8f0",
                        fontSize: 13,
                        color: "#64748b",
                        fontWeight: 500,
                    }}>
                        <span style={{ color: "#94a3b8", display: "flex" }}>{item.icon}</span>
                        {item.label}
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ─── Diff Stats ──────────────────────────────────────────────────────────── */

function DiffStats({ additions, deletions, changedFiles }: { additions?: number; deletions?: number; changedFiles?: number }) {
    if (additions == null && deletions == null && changedFiles == null) return null;

    return (
        <div style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 12,
            lineHeight: "18px"
        }}>
            {additions != null && (
                <span style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 3,
                    padding: "1px 7px",
                    borderRadius: 4,
                    background: "rgba(22, 163, 74, 0.08)",
                    color: "#16a34a",
                    fontWeight: 600,
                    fontFamily: MONO_FONT,
                    fontSize: 11.5
                }}>
                    +{additions}
                </span>
            )}
            {deletions != null && (
                <span style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 3,
                    padding: "1px 7px",
                    borderRadius: 4,
                    background: "rgba(220, 38, 38, 0.08)",
                    color: "#dc2626",
                    fontWeight: 600,
                    fontFamily: MONO_FONT,
                    fontSize: 11.5
                }}>
                    &minus;{deletions}
                </span>
            )}
            {changedFiles != null && (
                <span style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    color: "#6b7280",
                    fontSize: 11.5,
                    fontWeight: 500
                }}>
                    <FileCode size={11} style={{ opacity: 0.6 }} />
                    {changedFiles} file{changedFiles !== 1 ? "s" : ""}
                </span>
            )}
            {additions != null && deletions != null && (additions + deletions) > 0 && (
                <DiffBar additions={additions} deletions={deletions} />
            )}
        </div>
    );
}

function DiffBar({ additions, deletions }: { additions: number; deletions: number }) {
    const total = additions + deletions;
    const blocks = 5;
    const addBlocks = Math.max(total > 0 ? Math.round((additions / total) * blocks) : 0, additions > 0 ? 1 : 0);

    return (
        <span style={{ display: "inline-flex", gap: 1, marginLeft: 2 }}>
            {Array.from({ length: blocks }).map((_, i) => (
                <span
                    key={i}
                    style={{
                        width: 7,
                        height: 7,
                        borderRadius: 1.5,
                        background: i < addBlocks ? "#22c55e" : "#ef4444"
                    }}
                />
            ))}
        </span>
    );
}

function BranchMergeFlow({ branch, baseBranch }: { branch: string; baseBranch: string }) {
    return (
        <div style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "4px 10px",
            borderRadius: 6,
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            fontSize: 12,
            lineHeight: "18px",
            maxWidth: "100%",
            overflow: "hidden"
        }}>
            <GitMerge size={12} style={{ color: "#8b5cf6", flexShrink: 0 }} />
            <span style={{
                fontWeight: 600,
                color: "#334155",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontFamily: MONO_FONT,
                fontSize: 11.5
            }}>
                {branch}
            </span>
            <ArrowRight size={11} style={{ color: "#94a3b8", flexShrink: 0 }} />
            <span style={{
                fontWeight: 600,
                color: "#8b5cf6",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontFamily: MONO_FONT,
                fontSize: 11.5
            }}>
                {baseBranch}
            </span>
        </div>
    );
}

/* ─── Active Preview Card ─────────────────────────────────────────────────── */

const ActivePreviewCard = React.memo(function ActivePreviewCard({
    preview,
    destroying,
    onDestroy,
    clusters
}: {
    preview: PreviewWithGitMetadata;
    destroying: boolean;
    onDestroy: () => void;
    clusters?: Array<{ id: string; name: string }>;
}) {
    const status = PREVIEW_STATUS_MAP[preview.status] || PREVIEW_STATUS_MAP.DESTROYED;
    const isFailed = preview.status === "FAILED";
    const isInProgress = preview.status === "BUILDING" || preview.status === "DEPLOYING" || preview.status === "DESTROYING";
    const d = getPreviewDisplay(preview);

    const hasDiffStats = d.additions != null || d.deletions != null || d.changedFiles != null;
    const hasMergeTarget = d.isPR && d.branch && d.baseBranch;
    const isTagDuplicateTitle = !d.isPR && d.title === d.label;

    const cardBorder = isFailed ? "rgba(239, 68, 68, 0.25)" : "#E5E7EB";
    const cardBg = isFailed ? "rgba(239, 68, 68, 0.02)" : "#ffffff";

    return (
        <div style={{
            background: cardBg,
            border: `1px solid ${cardBorder}`,
            borderRadius: 12,
            padding: "20px 22px",
            transition: "all 0.15s ease",
            display: "flex",
            flexDirection: "column",
            position: "relative"
        }}
            onMouseEnter={e => {
                e.currentTarget.style.borderColor = isFailed ? "rgba(239, 68, 68, 0.4)" : "#C4C9CF";
                e.currentTarget.style.boxShadow = isFailed
                    ? "0 4px 12px rgba(239, 68, 68, 0.08)"
                    : "0 4px 12px rgba(0, 0, 0, 0.06)";
                e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={e => {
                e.currentTarget.style.borderColor = cardBorder;
                e.currentTarget.style.boxShadow = "none";
                e.currentTarget.style.transform = "translateY(0)";
            }}
        >
            {/* Header: PR/Tag badge + Status pill */}
            <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 10,
                gap: 12
            }}>
                {d.url ? (
                    <a
                        href={d.url}
                        target="_blank"
                        rel="noreferrer"
                        onClick={e => e.stopPropagation()}
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "4px 10px",
                            borderRadius: 6,
                            background: "linear-gradient(135deg, #eff6ff, #f0f9ff)",
                            border: "1px solid #bfdbfe",
                            color: "#2563eb",
                            fontWeight: 700,
                            fontSize: 13,
                            textDecoration: "none",
                            transition: "all 0.15s"
                        }}
                        onMouseEnter={e => {
                            e.currentTarget.style.background = "linear-gradient(135deg, #dbeafe, #e0f2fe)";
                        }}
                        onMouseLeave={e => {
                            e.currentTarget.style.background = "linear-gradient(135deg, #eff6ff, #f0f9ff)";
                        }}
                    >
                        <GitPullRequest size={13} />
                        {d.label}
                    </a>
                ) : (
                    <span style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "4px 10px",
                        borderRadius: 6,
                        background: "linear-gradient(135deg, #f0fdf4, #ecfdf5)",
                        border: "1px solid #bbf7d0",
                        color: "#15803d",
                        fontWeight: 700,
                        fontSize: 13
                    }}>
                        <Tag size={13} />
                        {d.label}
                    </span>
                )}

                <div
                    aria-label={`Status: ${status.label}`}
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        padding: "2px 6px",
                        borderRadius: 3,
                        background: `${status.color}10`,
                        border: `1px solid ${status.color}20`,
                        color: status.color,
                        fontSize: 9,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        ...(isInProgress ? {
                            animation: "preview-pulse 2s ease-in-out infinite"
                        } : {})
                    }}
                >
                    <span style={{ display: "flex", alignItems: "center" }}>
                        {status.icon}
                    </span>
                    {status.label}
                </div>
            </div>

            {/* Title */}
            {!isTagDuplicateTitle && d.title && (
                <div style={{
                    fontSize: 17,
                    fontWeight: 600,
                    color: "#111827",
                    lineHeight: 1.3,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    marginBottom: 8
                }}>
                    {d.title}
                </div>
            )}

            {/* PR: diff stats + branch merge flow */}
            {d.isPR && hasDiffStats && (
                <div style={{ marginBottom: 10 }}>
                    <DiffStats
                        additions={d.additions}
                        deletions={d.deletions}
                        changedFiles={d.changedFiles}
                    />
                </div>
            )}
            {hasMergeTarget && (
                <div style={{ marginBottom: 10 }}>
                    <BranchMergeFlow branch={d.branch} baseBranch={d.baseBranch!} />
                </div>
            )}

            {/* TAG: files changed + branch info */}
            {!d.isPR && (
                <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    marginBottom: 10,
                    fontSize: 12,
                    lineHeight: "18px"
                }}>
                    {d.changedFiles != null && (
                        <span style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            color: "#6b7280",
                            fontSize: 11.5,
                            fontWeight: 500
                        }}>
                            <FileCode size={11} style={{ opacity: 0.6 }} />
                            {d.changedFiles} file{d.changedFiles !== 1 ? "s" : ""} changed
                        </span>
                    )}
                    {d.branch && (
                        <>
                            {d.changedFiles != null && <span style={{ color: "#d1d5db" }}>·</span>}
                            <span style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                                fontSize: 11.5,
                                color: "#64748b"
                            }}>
                                <GitBranch size={11} style={{ color: "#8b5cf6" }} />
                                <span style={{ fontFamily: MONO_FONT, fontWeight: 600, color: "#334155" }}>
                                    {d.branch}
                                </span>
                            </span>
                        </>
                    )}
                </div>
            )}

            {/* Metadata row */}
            <div style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                flexWrap: "wrap",
                marginBottom: 10
            }}>
                <PreviewMetaItem variant="default" icon={<User size={12} />} text={d.author} />
                <span style={{ color: "#9CA3AF", fontSize: 12 }}>·</span>
                <PreviewMetaItem variant="default" icon={<Clock size={12} />} text={dayjs(preview.dateCreated).fromNow()} />
                <span style={{ color: "#9CA3AF", fontSize: 12 }}>·</span>
                <PreviewMetaItem
                    variant="default"
                    icon={<GitCommitHorizontal size={12} />}
                    text={d.headSha?.substring(0, 7)}
                    mono
                    copyable={d.headSha}
                />
            </div>

            {/* Cluster & Namespace */}
            {(preview.namespace || preview.scopedClusterId) && (() => {
                const clusterName = clusters?.find(c => c.id === preview.scopedClusterId)?.name;
                const tooltipContent = (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 12 }}>
                        {preview.scopedClusterId && (
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span style={{ color: "rgba(255,255,255,0.5)", minWidth: 68 }}>Cluster</span>
                                <span style={{ fontWeight: 600 }}>{clusterName || preview.scopedClusterId}</span>
                            </div>
                        )}
                        {preview.namespace && (
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span style={{ color: "rgba(255,255,255,0.5)", minWidth: 68 }}>Namespace</span>
                                <span style={{ fontWeight: 600, fontFamily: MONO_FONT, fontSize: 11 }}>{preview.namespace}</span>
                            </div>
                        )}
                    </div>
                );
                return (
                    <Tooltip title={tooltipContent} placement="bottom">
                        <div style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "6px 10px",
                            borderRadius: 6,
                            background: "#f8fafc",
                            border: "1px solid #f1f5f9",
                            marginBottom: 10,
                            cursor: "default",
                            maxWidth: "100%",
                            overflow: "hidden"
                        }}>
                            <Server size={12} style={{ color: "#94a3b8", flexShrink: 0 }} />
                            <span style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0,
                                overflow: "hidden",
                                fontSize: 12,
                                lineHeight: "18px"
                            }}>
                                {clusterName && (
                                    <>
                                        <span style={{
                                            color: "#94a3b8",
                                            fontWeight: 500,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            flexShrink: 1
                                        }}>
                                            {clusterName}
                                        </span>
                                        {preview.namespace && (
                                            <span style={{ color: "#d1d5db", margin: "0 6px", flexShrink: 0 }}>/</span>
                                        )}
                                    </>
                                )}
                                {preview.namespace && (
                                    <span style={{
                                        fontFamily: MONO_FONT,
                                        fontWeight: 600,
                                        fontSize: 11.5,
                                        color: "#475569",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        flexShrink: 1
                                    }}>
                                        {preview.namespace}
                                    </span>
                                )}
                            </span>
                        </div>
                    </Tooltip>
                );
            })()}

            {/* Spacer */}
            <div style={{ flex: 1, minHeight: 8 }} />

            {/* Footer with destroy action */}
            <div style={{
                marginTop: "auto",
                paddingTop: 14,
                borderTop: "1px solid #F3F4F6"
            }}>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <Tooltip title="Destroy preview">
                        <Button
                            type="text"
                            size="small"
                            danger
                            loading={destroying}
                            icon={<Trash2 size={13} />}
                            aria-label="Destroy preview"
                            onClick={e => {
                                e.stopPropagation();
                                onDestroy();
                            }}
                            style={{
                                width: 32,
                                height: 32,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: 8,
                                color: "#9CA3AF",
                                flexShrink: 0
                            }}
                        />
                    </Tooltip>
                </div>
            </div>
        </div>
    );
});

/* ─── History Row ─────────────────────────────────────────────────────────── */

const HistoryRow = React.memo(function HistoryRow({
    preview,
    isLast
}: {
    preview: PreviewWithGitMetadata;
    isLast: boolean;
}) {
    const { token } = theme.useToken();
    const d = getPreviewDisplay(preview);
    const status = PREVIEW_STATUS_MAP.DESTROYED;

    return (
        <div style={{
            display: "grid",
            gridTemplateColumns: "160px 90px minmax(120px, 1.2fr) minmax(80px, 1fr) 50px 60px 100px 110px",
            alignItems: "center",
            gap: 16,
            padding: "12px 20px",
            borderBottom: isLast ? "none" : `1px solid ${token.colorBorderSecondary}`,
            fontSize: 13,
            transition: "background 0.15s"
        }}
            onMouseEnter={e => { e.currentTarget.style.background = "#fafbfc"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
        >
            {/* PR/Tag badge */}
            <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {d.url ? (
                    <a
                        href={d.url}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                            color: "#2563eb",
                            fontWeight: 600,
                            fontSize: 12,
                            textDecoration: "none"
                        }}
                    >
                        <GitPullRequest size={12} style={{ flexShrink: 0 }} />
                        {d.label}
                    </a>
                ) : (
                    <span style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 5,
                        color: "#15803d",
                        fontWeight: 600,
                        fontSize: 12
                    }}>
                        <Tag size={12} style={{ flexShrink: 0 }} />
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {d.label}
                        </span>
                    </span>
                )}
            </div>

            {/* Status */}
            <div>
                <span style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    padding: "1px 6px",
                    borderRadius: 3,
                    background: `${status.color}10`,
                    border: `1px solid ${status.color}20`,
                    color: status.color,
                    fontSize: 9,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em"
                }}>
                    <span style={{ display: "flex", alignItems: "center" }}>{status.icon}</span>
                    {status.label}
                </span>
            </div>

            {/* Branch */}
            <div style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                overflow: "hidden",
                fontSize: 12,
                color: "#6b7280"
            }}>
                {d.branch ? (
                    <>
                        <GitBranch size={11} style={{ color: "#8b5cf6", flexShrink: 0 }} />
                        <span style={{
                            fontFamily: MONO_FONT,
                            fontWeight: 600,
                            color: "#334155",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                        }}>
                            {d.branch}
                        </span>
                        {d.isPR && d.baseBranch && (
                            <>
                                <ArrowRight size={9} style={{ color: "#94a3b8", flexShrink: 0 }} />
                                <span style={{
                                    fontFamily: MONO_FONT,
                                    fontWeight: 600,
                                    color: "#8b5cf6",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    flexShrink: 0
                                }}>
                                    {d.baseBranch}
                                </span>
                            </>
                        )}
                    </>
                ) : <span>&mdash;</span>}
            </div>

            {/* Description (PR title) */}
            <div style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                color: "#4b5563",
                fontSize: 12,
                fontWeight: 500
            }}>
                {d.isPR && d.title ? d.title : <span style={{ color: "#d1d5db" }}>&mdash;</span>}
            </div>

            {/* Changed files */}
            <span style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 3,
                color: "#9ca3af",
                fontSize: 11,
                fontWeight: 500
            }}>
                {d.changedFiles != null ? (
                    <>
                        <FileCode size={10} style={{ opacity: 0.6 }} />
                        {d.changedFiles}
                    </>
                ) : <span>&mdash;</span>}
            </span>

            {/* Commit */}
            <span style={{
                fontFamily: MONO_FONT,
                fontSize: 11,
                color: "#9ca3af",
                fontWeight: 500
            }}>
                {d.headSha ? d.headSha.substring(0, 7) : "\u2014"}
            </span>

            {/* Author */}
            <span style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                fontSize: 12,
                color: "#6b7280",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
            }}>
                {d.author ? (
                    <>
                        <User size={11} style={{ color: "#9ca3af", flexShrink: 0 }} />
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {d.author}
                        </span>
                    </>
                ) : <span>&mdash;</span>}
            </span>

            {/* Destroyed time */}
            <span style={{
                fontSize: 11,
                color: "#9ca3af",
                textAlign: "right"
            }}>
                {preview.dateDestroyed ? dayjs(preview.dateDestroyed).fromNow() : "\u2014"}
            </span>
        </div>
    );
});
